import React, { useEffect, useMemo, useState } from "react";

import {
  IoIosCheckmarkCircleOutline,
  IoIosCloseCircleOutline,
} from "react-icons/io";

import { toast } from "react-toastify";

import ContainedButton from "components/layout/form/containedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import { Method } from "axios";
import { getSideIdMain } from "../services/api";
import { useGaEvents } from "../hooks/useGaEvents";
import { useQuickBillContext } from "../hooks/useQuickBill";

import {
  AnswerForm,
  AttemptsLeft,
  Content,
  ContentButtons,
  ContentRight,
  Counter,
  Header,
  Paragraph,
  PopupContainer,
  PopupWrapper,
  QuestionTitle,
  Title,
} from "./styles";

const QuestionPool = ({
  body: { document },
  handleModal,
}: any): JSX.Element => {
  const [questionsPool, setQuestionsPool] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState("");
  const [currentQuestionId, setCurrentQuestionId] = useState("");
  const [questionCounter, setQuestionCounter] = useState(0);
  const [attemptsLeft, setAttemptsLeft] = useState(4);
  const [initialState, setInitialState] = useState(true);
  const [validatedQuestions, setValidatedQuestions] = useState(false);
  const [hasAttemptsLeft, setHasAttemptsLeft] = useState(true);
  const [answerInput, setAnswerInput] = useState("");
  const [answers, setAnswers] = useState([]);
  const [accessToken, setAccessToken] = useState("");

  const {
    setRequestPool,
    selectedUnit,
    document: documentContext,
    setPoolToken,
  } = useQuickBillContext();

  const {
    responsePool,
    handleClickResponsePool,
    handleClickContinueResponsePool,
    handleClickRetryResponsePool,
  } = useGaEvents();

  const idsToCheck = [
    "750d57f8-2b90-43ca-9ebd-49660dc56b5d",
    "7d50cd5d-30b4-4b78-bcdf-1c9dc7989c5f",
  ];

  const hasCep = idsToCheck.some((id) => currentQuestionId === id);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    if (hasCep) {
      if (value.length <= 3 && /^\d*$/.test(value)) {
        setAnswerInput(value);
      }
    } else {
      setAnswerInput(value);
    }
  };

  useEffect(() => {
    if (!accessToken) return;
    if (questionsPool.length === 0) {
      getQuestions();
    }

    if (answers.length === 2) {
      sendAnswers();
    }
  }, [questionsPool, answers, accessToken]);

  const getQuestions = async (): Promise<void> => {
    try {
      const result = await api.get(
        `industrial/customer/v1/questions?quantity=2`,
        {
          headers: {
            access_token: accessToken,
          },
        }
      );

      setQuestionsPool(result.data);
      setCurrentQuestion(result.data[0]?.question);
      setCurrentQuestionId(result.data[0]?.id);
    } catch (err) {
      const erro = err.response;
      if (erro) {
        const { message } = err.response.data;
        if (err.response.status !== 401) {
          toast.error(message);
        }
      } else {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const nextQuestion = (): void => {
    handleClickResponsePool(documentContext, selectedUnit);
    if (answerInput?.length < 3 && hasCep) return;

    if (answerInput) {
      saveAnswers(answerInput, questionsPool[questionCounter].id);
    }

    if (answerInput && questionCounter < 1) {
      setCurrentQuestion(questionsPool[1].question);
      setCurrentQuestionId(questionsPool[1].id);
      setAnswerInput("");
      setQuestionCounter((prevState) => prevState + 1);
    }
  };

  const saveAnswers = (answer, questionId): void => {
    const dontHaveAnswerId = !answers.some(
      (answer) => answer.questionId === questionId
    );

    if (dontHaveAnswerId) {
      setAnswers((prevState) => [
        ...prevState,
        { questionId, answer: answer.trim() },
      ]);
    }
  };

  const sendAnswers = async (): Promise<void> => {
    const siteId =
      selectedUnit?.siteId || (await getSideIdMain(document)) || "";

    const body = {
      document,
      answers,
      siteId,
    };

    setRequestPool(body);

    const headers = {
      headers: {
        access_token: accessToken,
        "Access-Control-Expose-Headers": "attempts_left",
      },
    };

    try {
      const response = await api.post(
        `industrial/customer/v1/questions/answers`,
        body,
        headers
      );

      setPoolToken(response?.data?.token);

      setInitialState(false);
      setValidatedQuestions(true);
      toast.success("Todas as respostas estão corretas!");
      responsePool(
        documentContext,
        selectedUnit,
        "Todas as respostas estão corretas!"
      );
    } catch (err) {
      setInitialState(false);
      setValidatedQuestions(false);
      setAttemptsLeft(err.response.headers.attempts_left);
      setRequestPool(null);

      if (err) {
        if (err.response.data.code === "422.051") {
          setHasAttemptsLeft(false);
        } else if (err.response.status === 422) {
          toast.error(err.response.data.message);
        }
      } else if (err.response.status !== 401) {
        toast.error("Ocorreu um erro interno na aplicação");
      }
    }
  };

  const tryAgain = (): void => {
    if (hasAttemptsLeft) {
      handleClickRetryResponsePool(documentContext, selectedUnit);
      setAnswerInput("");
      setInitialState(true);
      setAnswers([]);
      setQuestionsPool([]);
      setValidatedQuestions(false);
      setQuestionCounter(0);
      setRequestPool(null);
    } else {
      handleModal(false);
    }
  };

  const generateAccessToken = async (): Promise<void> => {
    const header = {
      "Content-Type": "application/json",
      "Accept-Language": "pt-BR",
      Authorization: process.env.REACT_APP_SIGNIN_AUTHORIZATION,
    };
    const parameters = {
      method: "POST" as Method,
      headers: header,
    };
    const body = {
      grant_type: "client_credentials",
    };

    try {
      const result = await api.post("oauth/access-token", body, parameters);

      setAccessToken(result.data.access_token);
    } catch (err) {
      const message =
        err.response.data[0]?.message || err.response.data.message;

      toast.error(message);
    }
  };

  const handleCloseModal = async (): Promise<void> => {
    handleModal(false);
  };

  useEffect(() => {
    (async () => {
      await generateAccessToken();
    })();
  }, []);

  const renderBody = useMemo(() => {
    if (initialState) {
      return (
        <ContentButtons>
          <TextField
            name="answer"
            label="Sua resposta aqui"
            placeholder="Sua resposta aqui"
            type={hasCep ? "numeric" : "text"}
            value={answerInput}
            inputMode={hasCep ? "numeric" : "text"}
            onChange={handleInputChange}
          />
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => nextQuestion()}
          >
            {answers.length >= 1 ? "Enviar" : "Próximo"}
          </ContainedButton>
        </ContentButtons>
      );
    }

    if (validatedQuestions) {
      return (
        <Content>
          <AttemptsLeft>
            <IoIosCheckmarkCircleOutline size={84} color="#4FFB23" />
            <section>
              <p>Validado com sucesso!</p>
              <p>Clique em continuar para ter acesso aos seus boletos.</p>
            </section>
          </AttemptsLeft>
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => {
              handleClickContinueResponsePool(documentContext, selectedUnit);
              handleCloseModal();
            }}
          >
            Continuar
          </ContainedButton>
        </Content>
      );
    }

    if (hasAttemptsLeft) {
      return (
        <Content>
          <AttemptsLeft>
            <IoIosCloseCircleOutline size={84} color="#F25A28" />
            <section>
              <p>Não foi possível continuar.</p>
              <p>Você precisa responder corretamente às perguntas!</p>
              <p>Você tem mais {attemptsLeft || 0} tentativas.</p>
            </section>
          </AttemptsLeft>
          <ContainedButton
            block
            type="button"
            continue
            width="100%"
            onClick={() => tryAgain()}
          >
            {attemptsLeft > 0 ? "Tente novamente" : "Fechar"}
          </ContainedButton>
        </Content>
      );
    }

    return (
      <Content>
        <AttemptsLeft>
          <IoIosCloseCircleOutline size={84} color="#F25A28" />
          <section>
            <p>
              Você foi bloqueado pois excedeu o número máximo de 5 de
              tentativas.
            </p>
            <p>Converse com nossos especialistas pelo Chat Online.</p>
            <p>
              Nosso time está conectado 24 horas por dia e 7 dias por semana
              para melhor atendê-lo.
            </p>
          </section>
        </AttemptsLeft>
      </Content>
    );
  }, [
    initialState,
    attemptsLeft,
    hasAttemptsLeft,
    validatedQuestions,
    answers,
    answerInput,
  ]);

  return (
    <PopupWrapper>
      <PopupContainer>
        <Header>
          <Title>Boleto rápido (Perguntas de validação)</Title>
          <ContentRight>
            {initialState && <Counter>{questionCounter + 1}/2</Counter>}
          </ContentRight>
        </Header>
        {initialState && (
          <Paragraph>
            Responda as perguntas corretamente para conseguir visualizar seus
            boletos.
          </Paragraph>
        )}
        {initialState && <QuestionTitle>{currentQuestion}</QuestionTitle>}
        <AnswerForm
          initialState={initialState}
          onSubmit={(e) => e.preventDefault()}
        >
          {renderBody}
        </AnswerForm>
      </PopupContainer>
    </PopupWrapper>
  );
};

export default QuestionPool;
